export function buildProjectBridgeCurrentTextMarkup(value, overlaps = []) {
    const text = `${value || ""}`
    if (!text.trim()) return ""
    const markup = looksLikeHtmlContent(text) ? text : escapeHtml(normalizeLineEndings(text)).replace(/\n/g, "<br />")
    return highlightOverlapMarkup(markup, overlaps)
}

function looksLikeHtmlContent(value) {
    return /<\/?(p|br|ol|ul|li|table|thead|tbody|tfoot|tr|td|th|strong|em|b|i|u|span|div)\b[^>]*>/i.test(
        `${value || ""}`
    )
}

function normalizeLineEndings(value) {
    return `${value || ""}`.replace(/\r\n/g, "\n").replace(/\r/g, "\n")
}

function escapeHtml(value) {
    return `${value || ""}`
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
}

function highlightOverlapMarkup(markup, overlaps) {
    const normalizedPhrases = getNormalizedOverlapPhrases(overlaps)
    if (!markup || normalizedPhrases.length === 0 || typeof DOMParser === "undefined") return markup

    const document = new DOMParser().parseFromString(`<div>${markup}</div>`, "text/html")
    const container = document.body.firstElementChild
    if (!container) return markup

    const textNodes = listTextNodes(document, container)
    const matchRanges = findContainerMatchRanges(textNodes, normalizedPhrases)
    if (matchRanges.length === 0) return markup

    applyHighlightRanges({
        document,
        textNodes,
        matchRanges,
    })

    return container.innerHTML
}

function getNormalizedOverlapPhrases(overlaps) {
    const uniquePhrases = new Map()

    ;(Array.isArray(overlaps) ? overlaps : []).forEach(function addOverlap(item) {
        buildPhraseVariantsList(item?.text).forEach(function addPhraseVariant(phrase) {
            if (!phrase.spaced && !phrase.compact) return
            uniquePhrases.set(`${phrase.spaced}::${phrase.compact}`, phrase)
        })
    })

    return Array.from(uniquePhrases.values()).sort(compareByLengthDesc)
}

function compareByLengthDesc(a, b) {
    return getPhraseLength(b) - getPhraseLength(a)
}

function getPhraseLength(phrase) {
    return Math.max(phrase?.spaced?.length || 0, phrase?.compact?.length || 0)
}

function listTextNodes(document, container) {
    const items = []
    const showText = typeof NodeFilter === "undefined" ? 4 : NodeFilter.SHOW_TEXT
    const walker = document.createTreeWalker(container, showText)
    let nextNode = walker.nextNode()

    while (nextNode) {
        items.push(nextNode)
        nextNode = walker.nextNode()
    }

    return items
}

function findContainerMatchRanges(textNodes, normalizedPhrases) {
    const content = buildSearchableContent(textNodes)
    const spacedRanges = findMatchRangesForIndex(content.spacedIndex, getPhraseValues(normalizedPhrases, "spaced"))
    const compactRanges = findMatchRangesForIndex(content.compactIndex, getPhraseValues(normalizedPhrases, "compact"))
    return mergeRanges(spacedRanges.concat(compactRanges))
}

function buildSearchableContent(textNodes) {
    const nodeEntries = []
    let plainText = ""

    textNodes.forEach(function appendTextNode(textNode, index) {
        const textValue = textNode?.textContent || ""
        const start = plainText.length

        plainText += textValue
        nodeEntries.push({
            node: textNode,
            start,
            end: plainText.length,
        })

        if (index < textNodes.length - 1) {
            plainText += "\n"
        }
    })

    return {
        nodeEntries,
        spacedIndex: buildNormalizedIndex(plainText),
        compactIndex: buildCompactIndex(plainText),
    }
}

function getPhraseValues(phrases, key) {
    return phrases
        .map(function getPhraseValue(item) {
            return item?.[key]
        })
        .filter(Boolean)
}

function applyHighlightRanges({ document, textNodes, matchRanges }) {
    const searchableContent = buildSearchableContent(textNodes)
    const rangesByNode = buildRangesByNode(searchableContent.nodeEntries, matchRanges)

    searchableContent.nodeEntries.forEach(function updateNode(entry, index) {
        const ranges = rangesByNode.get(index)
        if (!ranges || ranges.length === 0) return

        const fragment = buildHighlightedFragment({
            document,
            value: entry.node?.textContent || "",
            ranges,
        })
        if (fragment) entry.node.parentNode?.replaceChild(fragment, entry.node)
    })
}

function buildRangesByNode(nodeEntries, ranges) {
    const rangesByNode = new Map()

    nodeEntries.forEach(function collectNodeRanges(entry, index) {
        const localRanges = ranges
            .map(function mapRange(range) {
                return clampRangeToNode(entry, range)
            })
            .filter(Boolean)

        if (localRanges.length > 0) {
            rangesByNode.set(index, mergeRanges(localRanges))
        }
    })

    return rangesByNode
}

function clampRangeToNode(entry, range) {
    const start = Math.max(entry.start, range.start)
    const end = Math.min(entry.end, range.end)
    if (start >= end) return null

    return {
        start: start - entry.start,
        end: end - entry.start,
    }
}

function buildHighlightedFragment({ document, value, ranges }) {
    if (ranges.length === 0) return null

    const fragment = document.createDocumentFragment()
    let cursor = 0

    ranges.forEach(function appendRange(range) {
        if (range.start > cursor) {
            fragment.append(document.createTextNode(value.slice(cursor, range.start)))
        }

        const mark = document.createElement("mark")
        mark.textContent = value.slice(range.start, range.end)
        fragment.append(mark)
        cursor = range.end
    })

    if (cursor < value.length) {
        fragment.append(document.createTextNode(value.slice(cursor)))
    }

    return fragment
}

function findMatchRangesForIndex(index, normalizedPhrases) {
    if (!index.normalized || normalizedPhrases.length === 0) return []

    const matches = []
    let cursor = 0

    while (cursor < index.normalized.length) {
        const nextMatch = findNextMatch(index.normalized, normalizedPhrases, cursor)
        if (!nextMatch) break

        matches.push({
            start: index.map[nextMatch.start],
            end: index.map[nextMatch.end - 1] + 1,
        })
        cursor = nextMatch.end
    }

    return mergeRanges(matches)
}

function findNextMatch(normalizedText, normalizedPhrases, cursor) {
    let bestMatch = null

    normalizedPhrases.forEach(function inspectPhrase(phrase) {
        const start = normalizedText.indexOf(phrase, cursor)
        if (start === -1) return

        const end = start + phrase.length
        if (!bestMatch || start < bestMatch.start || (start === bestMatch.start && phrase.length > bestMatch.length)) {
            bestMatch = { start, end, length: phrase.length }
        }
    })

    return bestMatch
}

function mergeRanges(ranges) {
    if (ranges.length < 2) return ranges

    const orderedRanges = ranges.slice().sort(function compareRanges(a, b) {
        if (a.start !== b.start) return a.start - b.start
        return a.end - b.end
    })

    return orderedRanges.reduce(function appendRange(result, range) {
        const lastRange = result[result.length - 1]
        if (!lastRange || range.start > lastRange.end) return result.concat(range)

        lastRange.end = Math.max(lastRange.end, range.end)
        return result
    }, [])
}

function buildNormalizedIndex(value) {
    let normalized = ""
    const map = []
    let previousWasSpace = true

    for (let index = 0; index < `${value || ""}`.length; index += 1) {
        const character = value[index]

        if (isWordCharacter(character)) {
            normalized += character.toLowerCase()
            map.push(index)
            previousWasSpace = false
            continue
        }

        if (isApostrophe(character)) continue

        if (!previousWasSpace && normalized) {
            normalized += " "
            map.push(index)
            previousWasSpace = true
        }
    }

    if (normalized.endsWith(" ")) {
        normalized = normalized.slice(0, -1)
        map.pop()
    }

    return { normalized, map }
}

function normalizeForMatching(value) {
    return buildNormalizedIndex(value).normalized
}

function buildCompactIndex(value) {
    let normalized = ""
    const map = []

    for (let index = 0; index < `${value || ""}`.length; index += 1) {
        const character = value[index]
        if (!isWordCharacter(character)) continue

        normalized += character.toLowerCase()
        map.push(index)
    }

    return { normalized, map }
}

function buildPhraseVariants(value) {
    return {
        spaced: normalizeForMatching(value),
        compact: normalizeForCompactMatching(value),
    }
}

function buildPhraseVariantsList(value) {
    const variants = [buildPhraseVariants(value)]
    const normalizedWords = normalizeForMatching(value).split(" ").filter(Boolean)
    const maxTrimmedWords = Math.min(3, normalizedWords.length - 5)

    for (let index = 1; index <= maxTrimmedWords; index += 1) {
        variants.push(buildPhraseVariants(normalizedWords.slice(index).join(" ")))
    }

    return variants
}

function normalizeForCompactMatching(value) {
    return buildCompactIndex(value).normalized
}

function isWordCharacter(value) {
    return /[\p{L}\p{N}]/u.test(value)
}

function isApostrophe(value) {
    return value === "'" || value === "’"
}
